import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { getDisplayZone } from 'common/types/GetDisplayZone';
import { blankKeyText } from '../../../constants/gridText';
import { KEY_ENTER } from '../../../constants/keyboardCodes';
import { FilterGenerator } from './FilterGenerator';

/**
 * Custom ag-grid set-like filter for columns that store an array of zone
 * strings (loadZones, dischargeZones, deliveryZones).
 *
 * Behaviour:
 *  - Collects all distinct zone display values from every row in the grid.
 *  - A row passes if ANY of its zones matches a checked filter value.
 *  - "(Blanks)" entry handles rows with an empty array.
 *
 * Usage in column definition:
 *   filter: ZonesFilter,
 *   filterParams: { field: 'loadZones' },  // optional — falls back to colDef.field
 */
class ZonesFilter extends Component {
    constructor(props) {
        super(props);
        this.state = {
            /** Set<string> of zone display values currently checked in the filter */
            selected: new Set(),
            /** All unique display values found across every row */
            availableValues: [],
            filterText: '',
        };
        // Synchronous mirror of state.selected — React 18 batches setState so this.state
        // can lag behind when ag-grid calls contract methods synchronously (see LocationsFilter.tree).
        this._selected = new Set();
        this._colField = null;
    }

    componentDidMount() {
        this._colField =
            this.props.filterParams?.field ||
            this.props.column?.getColDef()?.field;
        this._buildAvailableValues();
    }

    afterGuiAttached(params) {
        this._buildAvailableValues();
    }

    // ── ag-grid filter contract ──────────────────────────────────────────────

    isFilterActive() {
        return this._selected.size !== this.state.availableValues.length;
    }

    doesFilterPass({ data }) {
        const zones = data[this._colField];
        if (!zones || zones.length === 0) {
            return this._selected.has(blankKeyText);
        }
        return zones.some((z) =>
            this._selected.has(getDisplayZone(z, this._colField))
        );
    }

    getModel() {
        if (!this.isFilterActive()) return null;
        return { values: [...this._selected] };
    }

    setModel(model) {
        if (!model) {
            this._selectAll();
            return;
        }
        this._selected = new Set(model?.values ?? []);
        this.setState({ selected: this._selected });
    }

    clearFilter() {
        this.setState({ filterText: '' });
    }

    // ── Internal helpers ─────────────────────────────────────────────────────

    _buildAvailableValues() {
        const seen = new Set();
        let hasBlanks = false;

        this.props.api?.forEachNode((node) => {
            if (!node.data) return;
            const zones = node.data[this._colField];
            if (!zones || zones.length === 0) {
                hasBlanks = true;
            } else {
                zones.forEach((z) =>
                    seen.add(getDisplayZone(z, this._colField))
                );
            }
        });

        const sorted = [...seen].sort((a, b) => a.localeCompare(b));
        if (hasBlanks) sorted.unshift(blankKeyText);

        const { availableValues } = this.state;
        const newSelected =
            this._selected.size === 0 ||
            this._selected.size === availableValues.length
                ? new Set(sorted)
                : this._selected;
        this._selected = newSelected;
        this.setState({ availableValues: sorted, selected: newSelected });
    }

    _toggle(value) {
        const newSelected = new Set(this._selected);
        if (newSelected.has(value)) {
            newSelected.delete(value);
        } else {
            newSelected.add(value);
        }
        this._selected = newSelected;
        this.setState({ selected: newSelected }, () =>
            this.props.filterChangedCallback()
        );
    }

    _selectAll() {
        const newSelected = new Set(this.state.availableValues);
        this._selected = newSelected;
        this.setState({ selected: newSelected }, () =>
            this.props.filterChangedCallback()
        );
    }

    _clearAll() {
        const empty = new Set();
        this._selected = empty;
        this.setState({ selected: empty }, () =>
            this.props.filterChangedCallback()
        );
    }

    _handleKeyDown = (event) => {
        if (event.keyCode === KEY_ENTER) {
            this._filterByVisibleValues();
        }
    };

    _filterByVisibleValues() {
        const { availableValues, filterText } = this.state;
        const lowerFilter = filterText.toLowerCase();
        const visible = filterText
            ? availableValues.filter((v) =>
                  v.toLowerCase().includes(lowerFilter)
              )
            : availableValues;

        const newSelected = new Set(visible);
        this._selected = newSelected;
        this.setState({ selected: newSelected }, () =>
            this.props.filterChangedCallback()
        );
    }

    // ── Render ───────────────────────────────────────────────────────────────

    render() {
        const { availableValues, selected, filterText } = this.state;
        const lowerFilter = filterText.toLowerCase();
        const visible = filterText
            ? availableValues.filter((v) =>
                  v.toLowerCase().includes(lowerFilter)
              )
            : availableValues;

        const allSelected =
            availableValues.length > 0 &&
            selected.size === availableValues.length;

        const elements = [
            <input
                key="search"
                className="ag-input-field-input ag-text-field-input"
                style={{ marginBottom: '10px' }}
                type="text"
                placeholder="Search…"
                value={filterText}
                onChange={(e) => this.setState({ filterText: e.target.value })}
                onKeyDown={this._handleKeyDown}
            />,
            [
                {
                    type: 'checkbox',
                    checked: allSelected,
                    onChange: () =>
                        allSelected ? this._clearAll() : this._selectAll(),
                    label: '(Select All)',
                },
                ...visible.map((value) => ({
                    type: 'checkbox',
                    checked: selected.has(value),
                    onChange: () => this._toggle(value),
                    label: value,
                })),
            ],
        ];

        return (
            <FilterGenerator
                elements={elements}
                clear={() => this._clearAll()}
            />
        );
    }
}

ZonesFilter.propTypes = {
    api: PropTypes.object,
    column: PropTypes.object,
    filterChangedCallback: PropTypes.func.isRequired,
    doesRowPassOtherFilter: PropTypes.func,
    filterParams: PropTypes.shape({
        /** Override for the field name to read zones from (defaults to colDef.field) */
        field: PropTypes.string,
    }),
};

export default ZonesFilter;
